/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import Imgix from 'js/components/Imgix';

import { Typography2 } from '@coursera/cds-core';
import { PlayIcon } from '@coursera/cds-icons';

import type { PreviewVideoData } from 'bundles/unified-description-page-common/contexts/PreviewVideoExperimentContext';

import _t from 'i18n!nls/unified-description-page-common';

type Props = {
  data: PreviewVideoData;
  onClick: () => void;
};

const styles = {
  button: css`
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    border: none;
    padding: 0;
    cursor: pointer;
    background: none;
    border-radius: var(--cds-border-radius-50);
    overflow: hidden;
    display: block;
  `,
  thumbnail: css`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  `,
  imageOverlay: css`
    position: absolute;
    inset: 0;
    background-color: var(--cds-color-utility-overlay);
  `,
  playOverlay: css`
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--cds-spacing-100);
  `,
  playButtonCircle: css`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background-color: var(--cds-color-utility-overlay);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.15s ease;

    button:hover & {
      transform: scale(1.08);
    }
  `,
  playIconWrapper: css`
    display: flex;
    color: var(--cds-color-neutral-background-primary);
  `,
  watchPreviewText: css`
    color: var(--cds-color-neutral-background-primary);
    font-size: 14px;
    line-height: 20px;
  `,
};

function HeroPreviewThumbnail({ data, onClick }: Props) {
  const poster = data.posterSrc ?? data.thumbnailLink;

  return (
    <button
      css={styles.button}
      onClick={onClick}
      aria-label={_t('Watch preview: #{name}', { name: data.name })}
      type="button"
    >
      {poster && <Imgix src={poster} alt={Imgix.DECORATIVE} imgParams={{ width: 900 }} css={styles.thumbnail} />}
      <div css={styles.imageOverlay} aria-hidden="true" />
      <div css={styles.playOverlay} aria-hidden="true">
        <div css={styles.playButtonCircle}>
          <div css={styles.playIconWrapper}>
            <PlayIcon size="large" />
          </div>
        </div>
        <Typography2 variant="bodySecondary" component="span" css={styles.watchPreviewText}>
          {_t('Watch preview')}
        </Typography2>
      </div>
    </button>
  );
}

HeroPreviewThumbnail.displayName = 'HeroPreviewThumbnail';

export default HeroPreviewThumbnail;
